import { useSiteContent } from '@/hooks/useSiteContent';
import ContentState from '@/components/feature/ContentState';

interface StandardsPoliciesData {
  title: string;
  highlight: string;
  subtitle: string;
  packages: { title: string; items: string[] }[];
  cancellation: { title: string; policies: { label: string; description: string }[] };
}

export default function StandardsPolicies() {
  const { data, loading, error, refresh } = useSiteContent<StandardsPoliciesData>(
    'why_us_standards_policies',
  );

  if (loading || error || !data) {
    return <ContentState loading={loading} error={error} ready={!!data} onRetry={refresh} />;
  }

  return (
    <section className="w-full bg-primary-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 text-center mb-4">
            {data.title}{' '}
            <span className="text-primary-700">{data.highlight}</span>
          </h2>
          <p className="text-center text-foreground-600 max-w-2xl mx-auto mb-12 md:mb-16 text-sm md:text-base">
            {data.subtitle}
          </p>

          {/* Package Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12">
            {data.packages.map((pkg) => (
              <div
                key={pkg.title}
                className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8"
              >
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-background-200/70">
                  <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-file-list-3-line text-white text-lg" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-foreground-950">
                    {pkg.title}
                  </h3>
                </div>
                <ul className="space-y-3">
                  {pkg.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className="ri-check-line text-primary-500 mt-0.5 shrink-0" />
                      <span className="text-sm text-foreground-700 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Cancellation Section */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
            <h3 className="text-lg md:text-xl font-bold text-foreground-950 text-center mb-8">
              {data.cancellation.title}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {data.cancellation.policies.map((policy) => (
                <div
                  key={policy.label}
                  className="bg-secondary-50 rounded-lg p-4 text-center"
                >
                  <span className="inline-block bg-primary-100 text-primary-700 text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-3">
                    {policy.label}
                  </span>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {policy.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}